import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Tooltip,
  Switch,
  Popover,
  Divider,
  IconButton,
  Checkbox
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { useTranslation } from 'react-i18next';
import { VehicleAutoSearch, AutoSearchState, AutoSearchMode, StartAutoSearchRequest } from '../../models/vehicleAutoSearch';
import { VehicleAutoNegoSession, AutoNegoState } from '../../models/vehicleAutoNegoSession';
import { startAutoSearch, endAutoSearch, startAutoNego, endAutoNego, getAutoSearchStatusColor, getAutoNegoStatusColor, getAutoSearchShortStatusLabel, getAutoNegoShortStatusLabel, parseAutoSearchError } from '../../services/autoSessionService';
import { SubscriptionRequiredError } from '../../services/apiClient';
import { useSubscriptionDialog } from '../../context/subscriptionDialogContext';
import { toast } from '../../utils/toast';
import { useAnalytics, useLocalStorage } from '../../hooks';

interface OneClickAIToggleProps {
  vehicleId: number;
  vehiclePlate?: string;
  autoSearchSession: VehicleAutoSearch | null;
  autoNegoSession: VehicleAutoNegoSession | null;
  loading?: boolean;
  onSuccess: () => void;
  onOpenSearchSettings?: () => void;
  onOpenNegoSettings?: () => void;
  compact?: boolean;
  // One-click AI toggle state (provided by parent)
  oneClickEnabled: boolean;
  setOneClickEnabled: (value: boolean) => void;
  oneClickServices: { search: boolean; nego: boolean };
  setOneClickServices: (value: { search: boolean; nego: boolean }) => void;
  // When false, the auto-sessions fetch failed recently — suppress live indicators since data is stale.
  autoSessionsHealthy?: boolean;
}

const OneClickAIToggle: React.FC<OneClickAIToggleProps> = ({
  vehicleId,
  vehiclePlate,
  autoSearchSession,
  autoNegoSession,
  loading = false,
  onSuccess,
  onOpenSearchSettings,
  onOpenNegoSettings,
  compact = false,
  oneClickEnabled,
  setOneClickEnabled,
  oneClickServices,
  setOneClickServices,
  autoSessionsHealthy = true,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { showSubscriptionDialog } = useSubscriptionDialog();
  const { trackAIAutomation } = useAnalytics();
  const [toggling, setToggling] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [autoSearchParams] = useLocalStorage<StartAutoSearchRequest>('autosearch-params', {
    auto_search_mode: AutoSearchMode.PREFERRED_DESTINATIONS,
  });

  const isSearchOn = autoSearchSession?.autoSearchState === AutoSearchState.ON;
  const isNegoOn = autoNegoSession?.autoNegoState === AutoNegoState.ON ||
    autoNegoSession?.autoNegoState === AutoNegoState.WAITING_RESULTS ||
    autoNegoSession?.autoNegoState === AutoNegoState.RESULT_TO_REVIEW;

  const isFullyOn = isSearchOn && isNegoOn;
  const isPartiallyOn = isSearchOn || isNegoOn;

  const isSearchActive = isSearchOn;
  const isSearchErrored = autoSearchSession?.autoSearchState === AutoSearchState.ERRORED;
  const searchErrorMessage = isSearchErrored
    ? parseAutoSearchError(autoSearchSession?.statusMessage, t)
    : null;

  const handleToggleAI = async (event: React.MouseEvent<HTMLElement>) => {
    // If one-click is disabled or already on, show popover with options
    if (!oneClickEnabled || isPartiallyOn) {
      setAnchorEl(event.currentTarget);
      return;
    }

    // Enable both
    await enableAI();
  };

  const enableAI = async () => {
    setToggling(true);
    try {
      // Start auto-search if saved in one-click services
      if (!isSearchOn && oneClickServices.search) {
        await startAutoSearch(vehicleId, {
          auto_search_mode: autoSearchParams.auto_search_mode ?? AutoSearchMode.PREFERRED_DESTINATIONS,
          full_truck_load: autoSearchParams.full_truck_load,
          should_return_reg_country: autoSearchParams.auto_search_mode === AutoSearchMode.PREFERRED_DESTINATIONS ? autoSearchParams.should_return_reg_country : undefined,
          selected_countries: autoSearchParams.auto_search_mode === AutoSearchMode.SELECTED_COUNTRIES ? autoSearchParams.selected_countries : undefined,
        });
        trackAIAutomation('search_started', { vehicle_id: vehicleId });
      }

      // Start auto-negotiation if saved in one-click services
      if (!isNegoOn && oneClickServices.nego) {
        await startAutoNego(vehicleId, {
          max_concurrent_negos: 3,
          target_successful_negos: 1,
        });
        trackAIAutomation('nego_started', { vehicle_id: vehicleId });
      }

      trackAIAutomation('enabled', { vehicle_id: vehicleId });
      toast.success(t('AI automation enabled for {{plate}}', { plate: vehiclePlate || `Vehicle ${vehicleId}` }));
      onSuccess();
    } catch (err: any) {
      if (err instanceof SubscriptionRequiredError) {
        showSubscriptionDialog(err.subscriptionType);
        return;
      }

      const errorKey = err?.errorData?.error?.additionalInfo || err?.errorData?.error?.errorKey;
      if (errorKey === 'client.extended.autoSearchMaxDailyReached') {
        toast.error(t('Daily automation limit reached for this truck'));
      } else if (errorKey === 'client.extended.autoSearchAlreadyStarted') {
        toast.info(t('AI automation is already running'));
      } else {
        toast.error(t('Failed to enable AI automation'));
      }
    } finally {
      setToggling(false);
    }
  };

  const disableAI = async () => {
    setToggling(true);
    setAnchorEl(null);
    try {
      // Stop both sessions
      if (isSearchOn) {
        await endAutoSearch(vehicleId, {
          cancel_negotiations: false,
          send_email: false,
        });
      }

      if (isNegoOn) {
        await endAutoNego(vehicleId, {
          cancel_negotiations: false,
          send_email: false,
        });
      }

      trackAIAutomation('disabled', { vehicle_id: vehicleId });
      toast.success(t('AI automation disabled'));
      onSuccess();
    } catch (_) {
      toast.error(t('Failed to disable AI automation'));
    } finally {
      setToggling(false);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOneClickToggle = (checked: boolean) => {
    setOneClickEnabled(checked);
  };

  const handleOneClickServiceToggle = (service: 'search' | 'nego', checked: boolean) => {
    const updated = { ...oneClickServices, [service]: checked };
    // Ensure at least one service is always selected
    if (!updated.search && !updated.nego) return;
    setOneClickServices(updated);
  };


  const getStatusText = () => {
    if (isFullyOn) return t('AI Active');
    if (isSearchOn && !isNegoOn) return t('Search Only');
    if (!isSearchOn && isNegoOn) return t('Nego Only');
    return t('AI Off');
  };

  const renderStatusRow = (
    icon: React.ReactNode,
    label: string,
    isOn: boolean,
    statusColor: string,
    onSettingsClick?: () => void,
    onSwitchChange?: () => void
  ) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1,
        borderRadius: 1,
        bgcolor: statusColor ? `${statusColor}15` : 'action.hover',
        border: '1px solid',
        borderColor: statusColor || 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="body2" fontWeight={500}>{label}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {onSettingsClick && (
          <IconButton size="small" onClick={onSettingsClick}>
            <SettingsIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        {onSwitchChange && (
          <Switch size="small" checked={isOn} onChange={onSwitchChange} />
        )}
      </Box>
    </Box>
  );

  const showSearchActivityDot = isSearchActive && !toggling && !isSearchErrored && autoSessionsHealthy;

  const renderSearchActivityDot = (top: number, right: number) => {
    if (!showSearchActivityDot) return null;
    return (
      <Box
        sx={{
          position: 'absolute',
          top,
          right,
          width: 7,
          height: 7,
          borderRadius: '50%',
          pointerEvents: 'none',
          bgcolor: (theme) => {
            if (isFullyOn) return theme.palette.mode === 'dark' ? '#22c55e' : '#15803d';
            return theme.palette.mode === 'dark' ? '#f59e0b' : '#b45309';
          },
          boxShadow: (theme) => `0 0 0 1.5px ${theme.palette.background.paper}`,
          animation: 'oneClickAiSearchPulse 1.6s ease-in-out infinite',
          '@keyframes oneClickAiSearchPulse': {
            '0%, 100%': { opacity: 0.45 },
            '50%': { opacity: 1 },
          },
        }}
      />
    );
  };

  const wrapWithTooltip = (content: React.ReactNode, session: any, statusLabel: string) => {
    if (!session?.statusMessage) return content;

    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{statusLabel}</Typography>
            <Box sx={{ height: '0.5rem' }} />
            <Box sx={{ height: '0.5rem' }} />
            <Typography variant="caption">{session.statusMessage}</Typography>
          </Box>
        }
        arrow
        enterDelay={1000}
      >
        <Box>{content}</Box>
      </Tooltip>
    );
  };

  // Rendered after both compact and non-compact branches so the AI Automation popover is reachable in either mode.
  const popover = (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: { p: 2, minWidth: 280, borderRadius: 2 }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t('AI Automation')}
        </Typography>
        <Tooltip title={t('Enable one-click start')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Checkbox
              size="small"
              checked={oneClickEnabled}
              onChange={(e) => handleOneClickToggle(e.target.checked)}
              sx={{ p: 0 }}
            />
          </Box>
        </Tooltip>
      </Box>

      {/* One-click service selection */}
      {oneClickEnabled && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            {t('One-click starts:')}
          </Typography>
          <Box
            onClick={() => handleOneClickServiceToggle('search', !oneClickServices.search)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              px: 1, py: 0.25, borderRadius: 1, cursor: 'pointer',
              bgcolor: oneClickServices.search ? 'primary.light' : 'action.disabledBackground',
              color: oneClickServices.search ? 'primary.contrastText' : 'text.disabled',
              fontSize: '0.7rem', fontWeight: 600,
              transition: 'all 0.15s',
              '&:hover': { opacity: 0.85 },
            }}
          >
            <SearchIcon sx={{ fontSize: 13 }} />
            {t('Search')}
          </Box>
          <Box
            onClick={() => handleOneClickServiceToggle('nego', !oneClickServices.nego)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              px: 1, py: 0.25, borderRadius: 1, cursor: 'pointer',
              bgcolor: oneClickServices.nego ? 'primary.light' : 'action.disabledBackground',
              color: oneClickServices.nego ? 'primary.contrastText' : 'text.disabled',
              fontSize: '0.7rem', fontWeight: 600,
              transition: 'all 0.15s',
              '&:hover': { opacity: 0.85 },
            }}
          >
            <HandshakeIcon sx={{ fontSize: 13 }} />
            {t('Nego')}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Auto Search Row */}
        {(autoSearchSession?.autoSearchState !== AutoSearchState.ON && autoSearchSession?.autoSearchState !== AutoSearchState.OFF)
          ? wrapWithTooltip(
            renderStatusRow(
              <SearchIcon sx={{ fontSize: 18, color: autoSearchSession ? getAutoSearchStatusColor(theme, autoSearchSession) : 'text.secondary' }} />,
              t('Auto Search'),
              isSearchOn,
              autoSearchSession ? getAutoSearchStatusColor(theme, autoSearchSession) : '',
              onOpenSearchSettings ? () => { handleClose(); onOpenSearchSettings(); } : undefined,
              async () => {
                if (isSearchOn) {
                  await endAutoSearch(vehicleId, { cancel_negotiations: false, send_email: false });
                  handleOneClickServiceToggle('search', false);
                } else {
                  await startAutoSearch(vehicleId, { auto_search_mode: AutoSearchMode.ANY });
                  handleOneClickServiceToggle('search', true);
                }
                onSuccess();
                handleClose();
              }
            ),
            autoSearchSession,
            autoSearchSession ? getAutoSearchShortStatusLabel(t, autoSearchSession.autoSearchState) : t('OFF')
          )
          : renderStatusRow(
            <SearchIcon sx={{ fontSize: 18, color: autoSearchSession ? getAutoSearchStatusColor(theme, autoSearchSession) : 'text.secondary' }} />,
            t('Auto Search'),
            isSearchOn,
            autoSearchSession ? getAutoSearchStatusColor(theme, autoSearchSession) : '',
            onOpenSearchSettings ? () => { handleClose(); onOpenSearchSettings(); } : undefined,
            async () => {
              if (isSearchOn) {
                await endAutoSearch(vehicleId, { cancel_negotiations: false, send_email: false });
                handleOneClickServiceToggle('search', false);
              } else {
                await startAutoSearch(vehicleId, { auto_search_mode: AutoSearchMode.ANY });
                handleOneClickServiceToggle('search', true);
              }
              onSuccess();
              handleClose();
            }
          )
        }

        {/* Auto Nego Row */}
        {(autoNegoSession?.autoNegoState !== AutoNegoState.ON && autoNegoSession?.autoNegoState !== AutoNegoState.OFF)
          ? wrapWithTooltip(
            renderStatusRow(
              <HandshakeIcon sx={{ fontSize: 18, color: autoNegoSession ? getAutoNegoStatusColor(theme, autoNegoSession) : 'text.secondary' }} />,
              t('Auto Negotiate'),
              isNegoOn,
              autoNegoSession ? getAutoNegoStatusColor(theme, autoNegoSession) : '',
              onOpenNegoSettings ? () => { handleClose(); onOpenNegoSettings(); } : undefined,
              async () => {
                if (isNegoOn) {
                  await endAutoNego(vehicleId, { cancel_negotiations: false, send_email: false });
                  handleOneClickServiceToggle('nego', false);
                } else {
                  await startAutoNego(vehicleId, { max_concurrent_negos: 3, target_successful_negos: 1 });
                  handleOneClickServiceToggle('nego', true);
                }
                onSuccess();
                handleClose();
              }
            ),
            autoNegoSession,
            autoNegoSession ? getAutoNegoShortStatusLabel(t, autoNegoSession.autoNegoState) : t('OFF')
          )
          : renderStatusRow(
            <HandshakeIcon sx={{ fontSize: 18, color: autoNegoSession ? getAutoNegoStatusColor(theme, autoNegoSession) : 'text.secondary' }} />,
            t('Auto Negotiate'),
            isNegoOn,
            autoNegoSession ? getAutoNegoStatusColor(theme, autoNegoSession) : '',
            onOpenNegoSettings ? () => { handleClose(); onOpenNegoSettings(); } : undefined,
            async () => {
              if (isNegoOn) {
                await endAutoNego(vehicleId, { cancel_negotiations: false, send_email: false });
                handleOneClickServiceToggle('nego', false);
              } else {
                await startAutoNego(vehicleId, { max_concurrent_negos: 3, target_successful_negos: 1 });
                handleOneClickServiceToggle('nego', true);
              }
              onSuccess();
              handleClose();
            }
          )
        }
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {isPartiallyOn ? (
        <Button
          size="small"
          color="error"
          variant="outlined"
          startIcon={<PowerSettingsNewIcon />}
          onClick={disableAI}
          fullWidth
        >
          {t('Stop All')}
        </Button>
      ) : (
        <Button
          size="small"
          color="success"
          variant="contained"
          startIcon={<SmartToyIcon />}
          onClick={() => {
            enableAI();
            handleClose();
          }}
          fullWidth
          disabled={toggling}
        >
          {t('Start All')}
        </Button>
      )}
    </Popover>
  );

  if (compact) {
    const compactTooltipTitle = isSearchErrored ? (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('Auto Search: ERROR')}</Typography>
        <Typography variant="caption">{searchErrorMessage}</Typography>
      </Box>
    ) : isSearchActive ? (
      `${getStatusText()} — ${t('Searching marketplace...')}`
    ) : (
      getStatusText()
    );

    return (
      <>
        <Tooltip arrow enterDelay={isSearchErrored ? 300 : 700} title={compactTooltipTitle}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <Box
              onClick={handleToggleAI}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: (theme) => {
                  if (isSearchErrored) {
                    return theme.palette.mode === 'dark' ? 'rgba(244,67,54,0.18)' : 'rgba(244,67,54,0.10)';
                  }
                  if (isFullyOn) {
                    return theme.palette.mode === 'dark'
                      ? 'rgba(34,197,94,0.18)'
                      : 'rgba(34,197,94,0.16)';
                  }
                  if (isPartiallyOn) {
                    return theme.palette.mode === 'dark'
                      ? 'rgba(245,158,11,0.20)'
                      : 'rgba(245,158,11,0.18)';
                  }
                  return theme.palette.mode === 'dark'
                    ? 'rgba(148,163,184,0.18)'
                    : 'rgba(148,163,184,0.22)';
                },

                border: '1px solid',
                borderColor: (theme) => {
                  if (isSearchErrored) {
                    return theme.palette.mode === 'dark' ? 'rgba(244,67,54,0.55)' : 'rgba(244,67,54,0.40)';
                  }
                  if (isFullyOn) {
                    return theme.palette.mode === 'dark' ? 'rgba(34,197,94,0.45)' : 'rgba(34,197,94,0.40)';
                  }
                  if (isPartiallyOn) {
                    return theme.palette.mode === 'dark' ? 'rgba(245,158,11,0.45)' : 'rgba(245,158,11,0.40)';
                  }
                  return theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.35)' : 'rgba(148,163,184,0.35)';
                },

                cursor: loading || toggling ? 'wait' : 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              }}
            >
              {toggling ? (
                <CircularProgress size={16} />
              ) : (
                <SmartToyIcon
                  sx={{
                    fontSize: 18,
                    color: (theme) => {
                      if (isSearchErrored) {
                        return theme.palette.mode === 'dark' ? '#EF9A9A' : '#C62828';
                      }
                      if (isPartiallyOn) {
                        return theme.palette.mode === 'dark' ? '#f59e0b' : '#b45309';
                      } else if (isFullyOn) {
                        return theme.palette.mode === 'dark' ? '#22c55e' : '#15803d';
                      } else {
                        return theme.palette.mode === 'dark' ? '#cbd5e1' : '#475569';
                      }
                    },

                  }}
                />
              )}
            </Box>
            {renderSearchActivityDot(1, 1)}
          </Box>
        </Tooltip>
        {popover}
      </>
    );
  }

  const buttonColor: 'error' | 'success' | 'warning' | 'primary' = isSearchErrored
    ? 'error'
    : isFullyOn
      ? 'success'
      : isPartiallyOn
        ? 'warning'
        : 'primary';

  let buttonStartIcon: React.ReactNode;
  let buttonLabel: string;
  if (toggling) {
    buttonStartIcon = <CircularProgress size={14} color="inherit" />;
    buttonLabel = '...';
  } else if (isSearchErrored) {
    buttonStartIcon = <SmartToyIcon sx={{ fontSize: 16 }} />;
    buttonLabel = t('Search error');
  } else {
    buttonStartIcon = <SmartToyIcon sx={{ fontSize: 16 }} />;
    buttonLabel = getStatusText();
  }

  const buttonTooltipTitle: React.ReactNode = isSearchErrored ? (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('Auto Search: ERROR')}</Typography>
      <Typography variant="caption">{searchErrorMessage}</Typography>
    </Box>
  ) : null;

  const renderedButton = (
    <Box sx={{ position: 'relative', display: 'inline-flex', minWidth: 0, maxWidth: '100%' }}>
      <Button
        variant={isFullyOn ? 'contained' : 'outlined'}
        color={buttonColor}
        onClick={handleToggleAI}
        disabled={loading || toggling}
        startIcon={buttonStartIcon}
        size="small"
        sx={{
          minWidth: 0,
          maxWidth: '100%',
          px: 1,
          py: 0.5,
          borderRadius: 1.5,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.75rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          '& .MuiButton-startIcon': {
            mr: 0.5,
            ml: 0,
            flexShrink: 0,
          },
        }}
      >
        <Box
          component="span"
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {buttonLabel}
        </Box>
      </Button>
      {renderSearchActivityDot(4, 4)}
    </Box>
  );

  return (
    <>
      {buttonTooltipTitle ? (
        <Tooltip arrow enterDelay={isSearchErrored ? 300 : 700} title={buttonTooltipTitle}>
          {renderedButton}
        </Tooltip>
      ) : (
        renderedButton
      )}
      {popover}
    </>
  );
};

export default OneClickAIToggle;
